/**
 * SYR-90 Phase 6A/7 — the concierge cockpit for The Hypothesis, shared between
 * the AI Hub subsystem page (every research subject) and the org details page
 * (that org's brands, auto-selected when there is only one).
 *
 * Phase 7 made the unit of work a research SUBJECT: a platform brand, or a
 * prospect that isn't on the platform at all (cold outreach). Research a
 * prospect from nothing but a name and a website, then link it to a CRM
 * account when it's worth pursuing — which is also what unlocks sending,
 * since outreach needs a company to send from and log against.
 *
 * Run the pipeline (full or re-form), optionally steer the AI with an admin
 * guidance note (injected as the final section of the formation input),
 * inspect the element scorecard, preview the exact email and confirm-send it
 * — the ONLY send path while automation is off.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    Card,
    Input,
    message,
    Modal,
    Popconfirm,
    Popover,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
} from 'antd';
import {
    DeleteOutlined,
    ExperimentOutlined,
    LinkOutlined,
    MailOutlined,
    PlusOutlined,
    RedoOutlined,
    SearchOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import { ReviewSourcesModal } from './ReviewSourcesModal';
import {
    useGetAdminBrandsQuery,
    useGetCrmCompaniesQuery,
} from '../../../services/nestjs-base-api';
import {
    aiAdminApi,
    HypothesisAdminDetail,
    HypothesisElementReading,
    HypothesisJobRow,
    HypothesisSignal,
    HypothesisSubjectRow,
    HypothesisTeasePreview,
} from '../../../services/ai-admin-api';

const { Text, Paragraph } = Typography;

export const HYPOTHESIS_STATUS_COLORS: Record<HypothesisJobRow['status'], string> = {
    QUEUED_RESEARCH: 'blue',
    RESEARCHING: 'processing',
    RESEARCH_DONE: 'green',
    // Gold: waiting on a person, which is neither progress nor a failure.
    AWAITING_REVIEWS: 'gold',
    FORMING: 'processing',
    READY: 'success',
    INSUFFICIENT_DATA: 'orange',
    FAILED: 'red',
    SKIPPED: 'default',
};

const SIGNAL_TAGS: Record<HypothesisSignal, { label: string; color: string }> = {
    STRENGTH: { label: 'Looks strong', color: 'green' },
    WATCH: { label: 'Worth watching', color: 'orange' },
    LIKELY_CONSTRAINT: { label: 'Likely constraint', color: 'purple' },
    INSUFFICIENT_SIGNAL: { label: "Can't tell yet", color: 'default' },
};

// RESEARCH_DONE belongs here: it is not a resting state — the next runner
// tick picks it up for formation — so the panel must keep polling through it
// rather than leaving the admin looking at a status that is already stale.
const RUNNING_STATUSES = [
    'QUEUED_RESEARCH',
    'RESEARCHING',
    'RESEARCH_DONE',
    'FORMING',
];

interface HypothesisRunnerPanelProps {
    jwt: string;
    /** Scope the brand list to one organisation (org details page). */
    organisationId?: number;
}

export const HypothesisRunnerPanel: React.FC<HypothesisRunnerPanelProps> = ({
    jwt,
    organisationId,
}) => {
    const [subjectId, setSubjectId] = useState<number | null>(null);
    // Review pages an admin found by hand, for when discovery misses.
    const [sourcesOpen, setSourcesOpen] = useState(false);
    const [subjects, setSubjects] = useState<HypothesisSubjectRow[]>([]);
    const [detail, setDetail] = useState<HypothesisAdminDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState<'full' | 'reform' | null>(null);
    const [preview, setPreview] = useState<HypothesisTeasePreview | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [guidance, setGuidance] = useState('');
    // Prospect creation (AI Hub only — a prospect belongs to no organisation).
    const [prospectOpen, setProspectOpen] = useState(false);
    const [prospectName, setProspectName] = useState('');
    const [prospectUrl, setProspectUrl] = useState('');
    const [creating, setCreating] = useState(false);
    // CRM promotion — also the gate on outreach.
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkCompanyId, setLinkCompanyId] = useState<number | null>(null);
    const [companySearch, setCompanySearch] = useState('');
    const [linking, setLinking] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Searchable brand picker — nobody knows brand IDs by heart. Scoped to
    // the org when organisationId is given. A brand with no subject yet is
    // researched through the brand trigger, which creates one.
    const [brandSearch, setBrandSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(brandSearch), 300);
        return () => clearTimeout(timer);
    }, [brandSearch]);
    const { data: brandsData, isFetching: brandsLoading } = useGetAdminBrandsQuery(
        {
            jwtToken: jwt,
            search: debouncedSearch || undefined,
            limit: 20,
            organizationId: organisationId,
        },
        { skip: !jwt },
    );
    const brandOptions = brandsData?.data ?? [];

    const { data: crmCompanies } = useGetCrmCompaniesQuery(
        { jwtToken: jwt, q: companySearch || undefined, limit: 20 },
        { skip: !jwt || !linkOpen },
    );

    const loadSubjects = useCallback(async () => {
        if (!jwt) return [] as HypothesisSubjectRow[];
        try {
            const rows = await aiAdminApi.listHypothesisSubjects(jwt);
            setSubjects(rows);
            return rows;
        } catch (e) {
            message.error((e as Error).message || 'Failed to load research subjects');
            return [] as HypothesisSubjectRow[];
        }
    }, [jwt]);

    useEffect(() => {
        void loadSubjects();
    }, [loadSubjects]);

    const load = useCallback(
        async (id: number, silent = false) => {
            if (!jwt) return;
            if (!silent) setLoading(true);
            try {
                const loaded = await aiAdminApi.getHypothesisSubjectDetail(jwt, id);
                setDetail(loaded);
                // Prefill the steering box with what the last run used.
                setGuidance((prev) => prev || loaded.job?.adminGuidance || '');
            } catch (e) {
                if (!silent) message.error((e as Error).message || 'Failed to load subject detail');
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [jwt],
    );

    const selectSubject = useCallback(
        (id: number | null) => {
            setSubjectId(id);
            setDetail(null);
            setGuidance('');
            if (id) void load(id);
        },
        [load],
    );

    // Org page with exactly one brand: skip the picker, select it silently.
    const orgBrandId =
        organisationId && !debouncedSearch && brandOptions.length === 1
            ? brandOptions[0].id
            : null;
    useEffect(() => {
        if (!orgBrandId || subjectId) return;
        const existing = subjects.find((s) => s.brandId === orgBrandId);
        if (existing) selectSubject(existing.id);
    }, [orgBrandId, subjectId, subjects, selectSubject]);

    // Light polling while the runner is working on this subject.
    useEffect(() => {
        const status = detail?.job?.status;
        if (!subjectId || !status || !RUNNING_STATUSES.includes(status)) return undefined;
        const timer = setInterval(() => load(subjectId, true), 5000);
        return () => clearInterval(timer);
    }, [subjectId, detail?.job?.status, load]);

    const run = async (mode: 'full' | 'reform') => {
        if (!subjectId) return;
        setRunning(mode);
        try {
            await aiAdminApi.triggerHypothesisForSubject(jwt, subjectId, {
                mode,
                adminGuidance: guidance.trim(),
            });
            message.success(
                mode === 'full'
                    ? 'Full research queued — the runner picks it up within a minute'
                    : 'Re-forming the hypothesis from stored research',
            );
            await load(subjectId, true);
        } catch (e) {
            message.error((e as Error).message || 'Trigger failed');
        } finally {
            setRunning(null);
        }
    };

    /**
     * A platform brand that has never been researched has no subject yet. The
     * brand trigger creates one, so the first run goes through that route and
     * everything after it is subject-keyed like any other.
     */
    const runForBrand = async (brandId: number) => {
        setRunning('full');
        try {
            await aiAdminApi.triggerHypothesisForBrand(jwt, brandId, {
                mode: 'full',
                adminGuidance: guidance.trim(),
            });
            message.success('Full research queued — the runner picks it up within a minute');
            const rows = await loadSubjects();
            const created = rows.find((s) => s.brandId === brandId);
            if (created) selectSubject(created.id);
        } catch (e) {
            message.error((e as Error).message || 'Trigger failed');
        } finally {
            setRunning(null);
        }
    };

    const createProspect = async () => {
        setCreating(true);
        try {
            const created = await aiAdminApi.createHypothesisProspect(
                jwt,
                prospectName.trim(),
                prospectUrl.trim(),
            );
            const rows = await loadSubjects();
            setProspectOpen(false);
            setProspectName('');
            setProspectUrl('');
            const row = rows.find((s) => s.id === created.id);
            if (row) selectSubject(row.id);
            message.success('Prospect added — run the research when you are ready');
        } catch (e) {
            message.error((e as Error).message || 'Could not add prospect');
        } finally {
            setCreating(false);
        }
    };

    const linkToCrm = async () => {
        if (!subjectId || !linkCompanyId) return;
        setLinking(true);
        try {
            await aiAdminApi.linkHypothesisSubjectToCrm(jwt, subjectId, linkCompanyId);
            await loadSubjects();
            await load(subjectId, true);
            setLinkOpen(false);
            setLinkCompanyId(null);
            message.success('Linked to the CRM account — outreach can now be sent');
        } catch (e) {
            message.error((e as Error).message || 'Link failed');
        } finally {
            setLinking(false);
        }
    };

    const deleteSubject = async () => {
        if (!subjectId) return;
        setDeleting(true);
        try {
            const result = await aiAdminApi.deleteHypothesisSubject(jwt, subjectId);
            selectSubject(null);
            await loadSubjects();
            message.success(
                `Deleted — ${result.hypotheses} hypothesis and ${result.jobs} job row removed with it`,
            );
        } catch (e) {
            message.error((e as Error).message || 'Delete failed');
        } finally {
            setDeleting(false);
        }
    };

    const openPreview = async () => {
        if (!subjectId) return;
        try {
            setPreview(await aiAdminApi.getHypothesisTeasePreview(jwt, subjectId));
            setPreviewOpen(true);
        } catch (e) {
            message.error((e as Error).message || 'Preview failed');
        }
    };

    const send = async () => {
        if (!subjectId) return;
        setSending(true);
        try {
            await aiAdminApi.sendHypothesisTease(
                jwt,
                subjectId,
                !!detail?.hypothesis?.teaseEmailSentAt,
            );
            message.success('Hypothesis email sent');
            setPreviewOpen(false);
            await load(subjectId, true);
        } catch (e) {
            message.error((e as Error).message || 'Send failed');
        } finally {
            setSending(false);
        }
    };

    const job = detail?.job ?? null;
    const hypothesis = detail?.hypothesis ?? null;
    const subject = detail?.subject ?? null;
    const isProspect = subject?.subjectType === 'PROSPECT';
    // Outreach needs a company to send from, log against and receive replies
    // into — the API refuses the send without one, so the button says so too.
    const sendBlockedReason =
        isProspect && !subject?.crmCompanyId
            ? 'Link this prospect to a CRM account before sending'
            : null;
    // A job stuck in a running status beyond the runner's lock timeout is an
    // orphan (e.g. the API restarted mid-research) — don't let it block
    // re-running forever.
    const STALE_RUNNING_MS = 10 * 60_000;
    const jobRunning =
        !!job &&
        RUNNING_STATUSES.includes(job.status) &&
        Date.now() - new Date(job.updatedAt).getTime() < STALE_RUNNING_MS;
    const canReform = !!job?.researchCompletedAt;
    // The org page's single-brand case auto-selects; everywhere else needs a
    // picker. Brands with no subject yet are offered separately below.
    const showBrandPicker = !(organisationId && brandOptions.length <= 1 && subjectId);
    return (
        <Card
            title={
                <span>
                    <ExperimentOutlined style={{ marginRight: 8 }} />
                    Concierge runner — research a brand, preview and send the email
                </span>
            }
            size="small"
            style={{ marginBottom: 16 }}
        >
            <Space wrap style={{ marginBottom: 12 }}>
                <Select
                    showSearch
                    allowClear
                    placeholder="Select a research subject…"
                    style={{ minWidth: 360 }}
                    value={subjectId ?? undefined}
                    optionFilterProp="label"
                    loading={loading}
                    onChange={(value?: number) => selectSubject(value ?? null)}
                    options={subjects
                        .filter((row) =>
                            organisationId
                                ? row.organisationId === organisationId
                                : true,
                        )
                        .map((row) => ({
                            value: row.id,
                            label: `${row.name}${row.subjectType === 'PROSPECT' ? ' (prospect)' : ''}${
                                row.constraintElement ? ` — ${row.constraintElement}` : ''
                            }`,
                        }))}
                />
                {!organisationId && (
                    <Button
                        size="small"
                        icon={<PlusOutlined style={{ marginRight: 4 }} />}
                        onClick={() => setProspectOpen(true)}
                        title="Research a brand that isn't on the platform"
                    >
                        Research a brand
                    </Button>
                )}
                <Button
                    size="small"
                    type="primary"
                    icon={<ThunderboltOutlined style={{ marginRight: 4 }} />}
                    onClick={() => run('full')}
                    loading={running === 'full'}
                    disabled={!subjectId || jobRunning}
                >
                    Run full research
                </Button>
                <Button
                    size="small"
                    icon={<RedoOutlined style={{ marginRight: 4 }} />}
                    onClick={() => run('reform')}
                    loading={running === 'reform'}
                    disabled={!subjectId || !canReform || jobRunning}
                    title="Re-runs only the AI formation from the stored research. No re-crawl, no SerpAPI spend."
                >
                    Re-form only
                </Button>
                {/* Same escape hatch the lead queue has: discovery misses in
                    ways no rule anticipates, so point the run at the page. */}
                <Button
                    size="small"
                    icon={<LinkOutlined style={{ marginRight: 4 }} />}
                    onClick={() => setSourcesOpen(true)}
                    disabled={!subjectId}
                    title="Review pages we could not find ourselves: a URL, or the page text for anything that blocks us"
                >
                    Review pages
                </Button>
                <Button
                    size="small"
                    icon={<MailOutlined style={{ marginRight: 4 }} />}
                    onClick={openPreview}
                    disabled={!subjectId || !hypothesis}
                >
                    Preview email
                </Button>
                {isProspect && (
                    <Button
                        size="small"
                        icon={<LinkOutlined style={{ marginRight: 4 }} />}
                        onClick={() => setLinkOpen(true)}
                    >
                        {subject?.crmCompanyId ? 'Change CRM account' : 'Link to CRM'}
                    </Button>
                )}
                {subject && (
                    <Popconfirm
                        title={`Delete the research for ${subject.name}?`}
                        description={
                            isProspect
                                ? 'The research and the hypothesis formed from it are removed. This cannot be undone.'
                                : "The research, the hypothesis and the customer's Briefing card are all removed. Their own Brand Knowledge is untouched. This cannot be undone."
                        }
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        onConfirm={deleteSubject}
                    >
                        <Button size="small" danger loading={deleting}>
                            <DeleteOutlined style={{ marginRight: 4 }} />
                            Delete
                        </Button>
                    </Popconfirm>
                )}
            </Space>

            {showBrandPicker && (
                <div style={{ marginBottom: 12 }}>
                    <Text
                        type="secondary"
                        style={{ fontSize: 12, display: 'block', marginBottom: 4 }}
                    >
                        <SearchOutlined style={{ marginRight: 4 }} />
                        Or start a platform brand that has never been researched
                    </Text>
                    <Select
                        showSearch
                        allowClear
                        placeholder={
                            organisationId
                                ? 'Select brand…'
                                : 'Search brand or organisation…'
                        }
                        style={{ minWidth: 360 }}
                        value={undefined}
                        filterOption={false}
                        onSearch={setBrandSearch}
                        loading={brandsLoading}
                        onChange={(value?: number) => {
                            if (!value) return;
                            const existing = subjects.find((row) => row.brandId === value);
                            if (existing) {
                                selectSubject(existing.id);
                            } else {
                                void runForBrand(value);
                            }
                        }}
                        options={brandOptions.map((brand) => ({
                            value: brand.id,
                            label: `${brand.name}${organisationId ? '' : ` — ${brand.organisationName}`}${brand.isMock ? ' (mock)' : ''}`,
                        }))}
                    />
                </div>
            )}

            {subject && (
                <Paragraph style={{ marginBottom: 8 }}>
                    <Tag color={isProspect ? 'gold' : 'blue'}>
                        {isProspect ? 'PROSPECT' : 'PLATFORM'}
                    </Tag>
                    <Text strong>{subject.name}</Text>
                    {subject.websiteUrl && (
                        <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                            {subject.websiteUrl}
                        </Text>
                    )}
                    {isProspect && (
                        <Tag
                            color={subject.crmCompanyId ? 'green' : 'default'}
                            style={{ marginLeft: 8 }}
                        >
                            {subject.crmCompanyId
                                ? `CRM #${subject.crmCompanyId}`
                                : 'not on the CRM'}
                        </Tag>
                    )}
                </Paragraph>
            )}

            <div style={{ margin: '0 0 12px', maxWidth: 720 }}>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                    Guidance for the AI (optional) — included in the next run
                </Text>
                <Input.TextArea
                    placeholder="e.g. 'the client is worried about price perception; weigh the premium positioning evidence carefully'"
                    value={guidance}
                    onChange={(e) => setGuidance(e.target.value)}
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    disabled={!subjectId}
                /></div>

            {job && (
                <Paragraph style={{ marginBottom: 8 }}>
                    <Tag color={HYPOTHESIS_STATUS_COLORS[job.status]}>{job.status}</Tag>
                    {jobRunning && <Spin size="small" style={{ marginRight: 8 }} />}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {[
                            job.evidenceScore !== null ? `evidence ${job.evidenceScore}` : null,
                            job.evidenceJson?.place?.listingCount
                                ? `${job.evidenceJson.place.listingCount} listing${job.evidenceJson.place.listingCount === 1 ? '' : 's'}`
                                : null,
                            job.evidenceJson?.place?.sampledLocations?.length
                                ? `${job.evidenceJson.place.sampledLocations.length} sampled`
                                : null,
                            job.evidenceJson?.reviewTextsCollected
                                ? `${job.evidenceJson.reviewTextsCollected} reviews`
                                : null,
                            job.evidenceJson?.competitors?.length
                                ? `${job.evidenceJson.competitors.length} competitor${job.evidenceJson.competitors.length === 1 ? '' : 's'}`
                                : null,
                        ]
                            .filter(Boolean)
                            .join(' · ')}
                    </Text>
                    {job.lastError && (
                        <Popover content={job.lastError} overlayStyle={{ maxWidth: 400 }}>
                            <Tag color="red" style={{ marginLeft: 8 }}>
                                last error
                            </Tag>
                        </Popover>
                    )}
                </Paragraph>
            )}

            {hypothesis && (
                <div>
                    <Paragraph style={{ marginBottom: 4 }}>
                        <Text strong>{hypothesis.headline}</Text>{' '}
                        <Tag color="purple">{hypothesis.constraintElement}</Tag>
                        {hypothesis.teaseEmailSentAt ? (
                            <Tag color="green">
                                email sent {new Date(hypothesis.teaseEmailSentAt).toLocaleString()}
                            </Tag>
                        ) : (
                            <Tag>email not sent</Tag>
                        )}
                        {hypothesis.dismissedAt && <Tag color="red">dismissed by customer</Tag>}
                    </Paragraph>
                    <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                        {hypothesis.claim}
                    </Paragraph>
                    {hypothesis.elementReadings.length > 0 && (
                        <Table
                            dataSource={hypothesis.elementReadings}
                            rowKey="element"
                            size="small"
                            pagination={false}
                            showHeader={false}
                            style={{ marginBottom: 8, maxWidth: 720 }}
                            columns={[
                                {
                                    dataIndex: 'element',
                                    width: 220,
                                    render: (element: string, row: HypothesisElementReading) => (
                                        <Text strong={row.signal === 'LIKELY_CONSTRAINT'}>
                                            {element}
                                        </Text>
                                    ),
                                },
                                {
                                    dataIndex: 'signal',
                                    width: 140,
                                    render: (signal: HypothesisSignal) => (
                                        <Tag color={SIGNAL_TAGS[signal].color}>
                                            {SIGNAL_TAGS[signal].label}
                                        </Tag>
                                    ),
                                },
                                {
                                    dataIndex: 'note',
                                    render: (note: string) => (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {note}
                                        </Text>
                                    ),
                                },
                            ]}
                        />
                    )}
                    {hypothesis.competitorsAnalysed.length > 0 && (
                        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 4 }}>
                            Competitors analysed: {hypothesis.competitorsAnalysed.join(', ')}
                        </Paragraph>
                    )}
                    <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
                        Based on {hypothesis.sourcesSummary}
                    </Paragraph>
                </div>
            )}

            {subjectId && !loading && detail && !job && !hypothesis && (
                <Text type="secondary">
                    No research has been run for this subject yet — run it above.
                </Text>
            )}

            <Modal
                title={preview?.subject}
                open={previewOpen}
                onCancel={() => setPreviewOpen(false)}
                width={720}
                footer={[
                    <Button key="close" onClick={() => setPreviewOpen(false)}>
                        Close
                    </Button>,
                    <Popconfirm
                        key="send"
                        title={
                            detail?.hypothesis?.teaseEmailSentAt
                                ? 'This email was ALREADY sent — send it again?'
                                : isProspect
                                  ? 'Send this cold outreach email to the CRM contact now?'
                                  : 'Send this email to the organisation now?'
                        }
                        okText="Send"
                        onConfirm={send}
                        disabled={!!detail?.hypothesis?.dismissedAt || !!sendBlockedReason}
                    >
                        <Button
                            type="primary"
                            icon={<MailOutlined style={{ marginRight: 4 }} />}
                            loading={sending}
                            disabled={!!detail?.hypothesis?.dismissedAt || !!sendBlockedReason}
                            title={sendBlockedReason ?? undefined}
                        >
                            {detail?.hypothesis?.teaseEmailSentAt ? 'Resend' : 'Send'}
                        </Button>
                    </Popconfirm>,
                ]}
            >
                {preview && (
                    <iframe
                        title="Tease email preview"
                        srcDoc={`<!DOCTYPE html><html lang="en"><body style="margin:0;padding:16px;font-family:Arial,Helvetica,sans-serif;color:#333;">${preview.bodyHtml}</body></html>`}
                        style={{ width: '100%', height: 560, border: '1px solid #eee', borderRadius: 4 }}
                    />
                )}
                {sendBlockedReason && (
                    <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
                        {sendBlockedReason}.
                    </Text>
                )}
            </Modal>

            <Modal
                title="Research a brand"
                open={prospectOpen}
                onCancel={() => setProspectOpen(false)}
                okText="Add"
                confirmLoading={creating}
                onOk={createProspect}
                okButtonProps={{
                    disabled: !prospectName.trim() || !prospectUrl.trim(),
                }}
            >
                <Paragraph type="secondary" style={{ fontSize: 12 }}>
                    For a brand that isn't on the platform. Research and formation work
                    exactly as they do for a customer; there is no card and no
                    confirm/overturn, because only responses can settle it.
                </Paragraph>
                <Input
                    placeholder="Brand name"
                    value={prospectName}
                    onChange={(e) => setProspectName(e.target.value)}
                    style={{ marginBottom: 8 }}
                />
                <Input
                    placeholder="https://example.com"
                    value={prospectUrl}
                    onChange={(e) => setProspectUrl(e.target.value)}
                />
            </Modal>

            <Modal
                title="Link to a CRM account"
                open={linkOpen}
                onCancel={() => setLinkOpen(false)}
                okText="Link"
                confirmLoading={linking}
                onOk={linkToCrm}
                okButtonProps={{ disabled: !linkCompanyId }}
            >
                <Paragraph type="secondary" style={{ fontSize: 12 }}>
                    Outreach sends through the CRM: personal From address, replies into
                    the shared inbox, and the send logged to the account timeline.
                </Paragraph>
                <Select
                    showSearch
                    placeholder="Search CRM accounts…"
                    style={{ width: '100%' }}
                    value={linkCompanyId ?? undefined}
                    filterOption={false}
                    onSearch={setCompanySearch}
                    onChange={(value?: number) => setLinkCompanyId(value ?? null)}
                    options={(crmCompanies?.data ?? []).map((company) => ({
                        value: company.id,
                        label: company.name,
                    }))}
                />
            </Modal>

            <ReviewSourcesModal
                jwt={jwt}
                subjectId={sourcesOpen ? subjectId : null}
                brandName={subjects.find((row) => row.id === subjectId)?.name}
                onClose={() => setSourcesOpen(false)}
                // Full research, not re-form: the pages have to be FETCHED
                // before formation has anything new to read.
                onSaved={() => run('full')}
            />
        </Card>
    );
};
